<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <RouterLink v-if="item" :to="{ name: 'stock-changes' }" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'chevron-left']" />
      <span class="d-none d-sm-inline-block ms-1">{{ $t('global.list') }}</span>
    </RouterLink>
    <RouterLink v-if="item" :to="{ name: 'item', params: { itemId: item.id }}" :title="$t('menu.items')" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'rectangle-list']" />
    </RouterLink>
    <RouterLink v-if="item" :to="{ name: 'stock-change', params: { itemId: item.id } }" :title="$t('menu.stock-changes')" class="btn btn-outline-secondary">
      <font-awesome-icon :icon="['fas', 'plus-minus']" />
    </RouterLink>
    <span v-if="item" class="input-group-text">
      <ItemField :item="item" />
    </span>
    <input v-if="!itemIdFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('field.generic.item')" :disabled="isLoading">
    <input v-if="!itemIdFilter && !ignoreSignature" type="search" class="form-control" v-model="lotFilter" :placeholder="$t('field.masterdata.lot')" :disabled="isLoading">
    <select v-if="!itemIdFilter" class="form-select" :class="{ 'filter-unset': supplierIdFilter === null }" v-model="supplierIdFilter" :disabled="isLoading" :title="$t('field.generic.supplier')">
      <option :value="null" class="filter-placeholder">- {{ $t('field.generic.supplier') }} -</option>
      <option v-for="supplier in sortedSuppliers" :key="supplier.id" :value="supplier.id">{{ supplier.name }}</option>
    </select>
    <select v-if="!itemIdFilter" class="form-select" :class="{ 'filter-unset': itemSourceFilter === '' }" v-model="itemSourceFilter" :disabled="isLoading" :title="$t('page.stock.itemSourceAll')">
      <option value="" class="filter-placeholder">- {{ $t('page.stock.itemSourceAll') }} -</option>
      <option value="melodi">{{ $t('page.stock.itemSourceMelodi') }}</option>
      <option value="external">{{ $t('page.stock.itemSourceExternal') }}</option>
    </select>
    <!-- <button class="btn" :class="{ 'btn-outline-primary': !micOpen, 'btn-primary': micOpen, 'd-none d-sm-block': true }" @click="startSpeechToTxt()" :disabled="isLoading">
      <font-awesome-icon v-if="!micOpen" :icon="['fas', 'microphone']" />
      <font-awesome-icon v-if="micOpen" :icon="['fas', 'microphone']" flip />
      {{ $t('action.asrRecognize') }}
    </button> -->
    <!-- <button class="btn" :class="{ 'btn-outline-primary': !speaking, 'btn-primary': speaking, 'd-none d-lg-block': true }" @click="startTxtToSpeech()" :disabled="isLoading">
      <font-awesome-icon v-if="!speaking" :icon="['fas', 'megaphone']" />
      <font-awesome-icon v-if="speaking" :icon="['fas', 'megaphone']" beat />
      {{ $t('action.ttsRead') }}
    </button> -->
    <a v-if="!ignoreSignature" class="btn btn-outline-primary d-none d-sm-block" href="#" @click.prevent="toggleEditMode" :disabled="isLoading">
      <font-awesome-icon :icon="['fas',  useEditMode ? 'floppy-disk' : 'edit']" />
    </a>
    <button
      v-if="userSettingsStore.data.developerMode"
      class="btn"
      :class="ignoreSignature ? 'btn-primary' : 'btn-outline-secondary'"
      :title="$t('action.ignoreSignature')"
      :aria-pressed="ignoreSignature"
      @click="toggleIgnoreSignature"
      :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'layer-group']" />
    </button>
    <a v-if="!item" class="btn btn-outline-primary d-none d-sm-block" href="#" @click.prevent="downloadCsv()" :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'file-csv']" />
    </a>
    <a v-if="!item" class="btn btn-outline-primary d-none d-sm-block" href="#" @click.prevent="downloadXlsx()" :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'file-excel']" />
    </a>
    <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="!item && (itemFilter || itemIdFilter || lotFilter || filterWord || supplierIdFilter || itemSourceFilter)">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
      {{ runtimeTranscription ?? filterWord }}
    </button>
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <StockList v-else :filtered-list="displayList" :use-edit-mode="useEditMode" :show-item-field="!item" :ignore-signature="ignoreSignature" />
</div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { toast } from "vue3-toastify";
import { useSessionStore, useStockStore, useItemsStore, useSuppliersStore, useSupplierItemsStore, useUserSettingsStore } from '@/stores';
import api from "@/services/api";
import { compareOptionalDate, compareOptionalString } from "@/services";
import FrontendStock from "@/models/frontend/Stock";
import StockList from "@/components/lists/StockList.vue";
import ISetStockRequest from "@/models/requests/ISetStockRequest.ts";
import Item from "@/models/frontend/Item";
import { ItemField } from "@/components/fields";

const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const { selectedSiteId } = storeToRefs(sessionStore);
const i18n = useI18n();

const stockStore = useStockStore();
const suppliersStore = useSuppliersStore();
const supplierItemsStore = useSupplierItemsStore();
const { data, error } = storeToRefs(stockStore);
const { data: suppliers } = storeToRefs(suppliersStore);
const { data: supplierItems } = storeToRefs(supplierItemsStore);
const isLoading = ref(false);
const sort = ref(false);
const baseSortedList = ref<FrontendStock[]>([]);
const itemFilter = ref("");
const itemIdFilter = ref(null as number | null);
const lotFilter = ref("");
const supplierIdFilter = ref(null as number | null);
const itemSourceFilter = ref("");
const useEditMode = ref(false);
const item = ref(null as Item | null);
const ignoreSignature = ref(false);

const suppliersWithStock = computed(() => {
  const itemIdsInStock = new Set(data.value.map(s => s.item.id));
  const supplierIds = new Set<number>();
  for (const si of supplierItems.value) {
    if (itemIdsInStock.has(si.item.id)) supplierIds.add(si.supplier.id);
  }
  return supplierIds;
});

const sortedSuppliers = computed(() =>
  [...suppliers.value]
    .filter(s => suppliersWithStock.value.has(s.id))
    .sort((a, b) => a.name.localeCompare(b.name))
);

const supplierItemIds = computed(() => {
  if (supplierIdFilter.value === null) return null;
  return new Set(supplierItemsStore.getSupplierItems(supplierIdFilter.value).map(si => si.item.id));
});

// Initialize itemIdFilter with route parameter
const initializeItemFilter = () => {
  const itemId = route.params.itemId as string;
  if (itemId) {
    const numericId = parseInt(itemId, 10);
    if (!isNaN(numericId)) {
      itemIdFilter.value = numericId;
      item.value = useItemsStore().getById(numericId);
    }
  }
};

// Watch for route changes
watch(() => route.params.itemId, (newItemId) => {
  if (newItemId) {
    const numericId = parseInt(newItemId as string, 10);
    if (!isNaN(numericId)) {
      itemIdFilter.value = numericId;
      item.value = useItemsStore().getById(numericId);
    }
  } else {
    itemIdFilter.value = null;
    item.value = null;
  }
});

// Watch for itemIdFilter changes and update route
watch(itemIdFilter, (newItemId) => {
  if (newItemId && newItemId.toString() !== route.params.itemId) {
    router.push({ name: 'stocks', params: { itemId: newItemId.toString() } });
  } else if (!newItemId && route.params.itemId) {
    router.push({ name: 'stocks' });
  }
});

const runtimeTranscription = ref(null as string | null);
const filterWord = ref(null as string | null);
const speechVoices = ref([] as SpeechSynthesisVoice[]);

onMounted(async () => {
  // Initialize itemFilter from route parameter
  initializeItemFilter();

  await refresh();
  await useItemsStore().fetchOpenOrdersQuantity();
  await suppliersStore.fetchAll();
  await supplierItemsStore.fetchAll();
  // this is a workaround for the speechSynthesis.getVoices() not being available immediately
  if ('speechSynthesis' in window) {
    speechVoices.value = window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = function() {
      speechVoices.value = window.speechSynthesis.getVoices();
    };
  }
});

watch(selectedSiteId, async () => {
  await useItemsStore().fetchOpenOrdersQuantity();
});

async function refresh() {
  error.value = "";
  isLoading.value = true;

  try {
    await stockStore.fetchAll();
  } finally {
    isLoading.value = false;
  }
}

const resetFilter = () => {
  filterWord.value = null;
  itemFilter.value = "";
  itemIdFilter.value = null;
  item.value = null;
  lotFilter.value = "";
  supplierIdFilter.value = null;
  itemSourceFilter.value = "";
  
  // Clear itemId from route if present
  if (route.params.itemId) {
    router.push({ name: 'stocks' });
  }
};

const downloadCsv = () => {
  const site = sessionStore.selectedSite;
  api.get(`v1.0/stocks/.csv?siteId=${selectedSiteId.value}`)
    .then(response => {
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0,19).replace(/[:T]/g, '-');
      link.href = URL.createObjectURL(blob);
      link.download = `stock-${site?.code}-${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    }
    ).catch(console.error)
};

const downloadXlsx = () => {
  const site = sessionStore.selectedSite;
  api.get<Blob>(`v1.0/stocks/.xlsx?siteId=${selectedSiteId.value}`, { responseType: 'blob' })
    .then(response => {
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0,19).replace(/[:T]/g, '-');
      link.href = URL.createObjectURL(blob);
      link.download = `stock-${site?.code}-${timestamp}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    }
    ).catch(console.error)
};

const toggleEditMode = () => {
  if (useEditMode.value) {
    syncChangedQuantities();
  }

  useEditMode.value = !useEditMode.value;
}

const toggleIgnoreSignature = () => {
  if (!ignoreSignature.value) {
    // Editing aggregated rows is meaningless; flush any pending edits and leave edit mode.
    if (useEditMode.value) {
      syncChangedQuantities();
      useEditMode.value = false;
    }
    // A residual lot filter would aggregate only matching signatures, producing partial totals.
    lotFilter.value = "";
  }
  ignoreSignature.value = !ignoreSignature.value;
}

const syncChangedQuantities = () => {
  isLoading.value = true;
  let success: boolean | undefined = undefined;
  
  filteredList.value.filter(stock => stock.modifiedQuantity != undefined).forEach((stock) => {
    success = success ?? true;
    
    const request: ISetStockRequest = {
      siteId: stock.site.id,
      itemId: stock.item.id,
      newQuantity: stock.modifiedQuantity!,
      lot: stock.lot,
      bestBefore: stock.bestBefore,
      expiry: stock.expiry
    };

    stockStore.postStockSet(request);
    
    if (stockStore.error) {
      success = false;
      toast.error(i18n.t("page.stock.errorMessage", { item: stock.item.code }), { autoClose: false });
      stockStore.error = null;
    }
    
    stock.modifiedQuantity = undefined;
  });

  if (success) {
    toast.info(i18n.t("page.stock.successMessage"), { autoClose: 3000 });
  }
  
  
  isLoading.value = false;
}

const sortedList = computed(() => {
  if (sort.value) {
    return baseSortedList.value
  }
  else {
    return [...data.value].sort((a, b) =>
      a.item.name.localeCompare(b.item.name) ||
      a.item.code.localeCompare(b.item.code) ||
      compareOptionalDate(a.expiryAsDate, b.expiryAsDate) ||
      compareOptionalDate(a.bestBeforeAsDate, b.bestBeforeAsDate) ||
      compareOptionalString(a.lot, b.lot)
    );
  }
});

const filteredList = computed(() => {
  let words = [] as string[];
  if (filterWord.value !== null) {
    words = filterWord?.value.toLocaleLowerCase().replace(/[^\wäöü\s]/g, '').split(' ');
  }

  return sortedList.value.filter((item) => {
    return (
      (item.item.name.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1 || item.item.code.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1) &&
      (!itemIdFilter.value || item.item.id === itemIdFilter.value) &&
      (!lotFilter.value || (item.lot && item.lot.toLowerCase().indexOf(lotFilter.value.toLowerCase()) != -1)) &&
      (itemSourceFilter.value !== 'melodi' || !item.item.isInternalGenerated) &&
      (itemSourceFilter.value !== 'external' || item.item.isInternalGenerated) &&
      (!supplierItemIds.value || supplierItemIds.value.has(item.item.id)) &&
      (words.length === 0 || words.some((element) => item.item.name.toLocaleLowerCase().split(' ').includes(element)))
    );
  });
});

const displayList = computed<FrontendStock[]>(() => {
  if (!ignoreSignature.value) {
    return filteredList.value;
  }

  // Aggregate per (site, item): sum quantity / estimatedQuantity across all signatures.
  // Iteration order is preserved from filteredList so the existing sort still applies.
  const groups = new Map<string, FrontendStock>();
  for (const stock of filteredList.value) {
    const key = `${stock.site.id}|${stock.item.id}`;
    const existing = groups.get(key);
    if (existing) {
      existing.quantity += stock.quantity;
      existing.estimatedQuantity += stock.estimatedQuantity;
    } else {
      groups.set(key, new FrontendStock(
        stock.site.id * 1_000_000 + stock.item.id,
        stock.site,
        stock.item,
        stock.quantity,
        stock.estimatedQuantity,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        false,
        false,
        false,
        undefined,
      ));
    }
  }
  return [...groups.values()];
});
</script>

<style scoped>
.form-select.filter-unset {
  font-style: italic;
  color: var(--bs-secondary-color);
}
.form-select.filter-unset option:not(.filter-placeholder) {
  font-style: normal;
  color: var(--bs-body-color);
}
.form-select option.filter-placeholder {
  font-style: italic;
  color: var(--bs-secondary-color);
}
</style>
